import { PlentyGoalAccountFinancialEntity } from '../../financialEntities';
import { FormulaItem } from '../../formula';
import { toDollarsWithCents } from '../../util/currency';
import { toPercentageStr } from '../../util/numbers';
import { capitalize } from '../../util/strings';
import { isNumber } from '../../util/types';
import { IconName } from '../icons';
import { useCurrentRateInfo } from '../rates';
import { getPartnerName, useCurrentUser } from '../user';
import {
    getContributedOrInvestedStr,
    getGoalAccountInvestmentInfo,
    getMoneyMarketRateStr,
    iconByGoalAccountInvestmentType,
    titleByGoalAccountInvestmentType,
} from './helpers';

export interface InvestmentInfoRowProps {
    label: string;
    value: string;
    valueIconName?: IconName;
    valueHighlightColorScheme?: 'green';
    infoTitle?: string;
    infoParagraphs?: string[];
    infoFormulaItems?: FormulaItem[];
}

export const useInvestmentInfoRowsProps = ({
    goalAccount,
}: {
    goalAccount: PlentyGoalAccountFinancialEntity;
}) => {
    const { currentRateInfo } = useCurrentRateInfo();
    const { partnerInfo } = useCurrentUser();
    const investmentInfo = getGoalAccountInvestmentInfo(goalAccount);
    let ownershipContext: 'JOINT' | 'MINE' | 'THEIRS';
    let partnerName;
    if (goalAccount.info.accountType === 'JOINT_BROKERAGE') {
        ownershipContext = 'JOINT';
    } else if (goalAccount.isMine) {
        ownershipContext = 'MINE';
    } else {
        ownershipContext = 'THEIRS';
        partnerName = getPartnerName(partnerInfo);
    }

    let posessiveStr: string;
    let pastPosessiveStr: string;
    if (ownershipContext === 'THEIRS') {
        posessiveStr = `${partnerName}'s`;
        pastPosessiveStr = `${partnerName} has`;
    } else if (ownershipContext === 'JOINT') {
        posessiveStr = `our joint`;
        pastPosessiveStr = `you both have`;
    } else {
        posessiveStr = `your`;
        pastPosessiveStr = `you have`;
    }

    const depositsAmount = investmentInfo.contributionsAmount ?? 0;
    const withdrawalsAmount = investmentInfo.withdrawalsAmount ?? 0;
    const balance = investmentInfo.balance;
    const netInvestedAmount = depositsAmount - withdrawalsAmount;

    // If member hasn't deposited any money yet, return must be $0
    const returnAmount = depositsAmount === 0 ? 0 : balance - netInvestedAmount;

    // If member hasn't deposited any money yet or would divide by 0, show 0%
    const rateOfReturn =
        depositsAmount === 0
            ? 0
            : (balance + withdrawalsAmount) / depositsAmount - 1;

    const depositLabel = getContributedOrInvestedStr(
        investmentInfo.investmentType
    );

    const investmentInfoRowsProps: InvestmentInfoRowProps[] = [
        {
            label: `Total ${depositLabel}`,
            value: toDollarsWithCents(depositsAmount),
            infoTitle: `Total ${depositLabel}`,
            infoParagraphs: [
                `Total ${depositLabel} is the total amount of money that ${pastPosessiveStr} deposited into this goal account.`,
            ],
        },
        {
            label: 'Total withdrawn',
            value: toDollarsWithCents(withdrawalsAmount),
            infoTitle: 'Total withdrawn',
            infoParagraphs: [
                `Total withdrawn is the total amount of money that ${pastPosessiveStr} withdrawn from this goal, if any.`,
            ],
        },
    ];

    if (investmentInfo.investmentType === 'HIGH_YIELD_CASH') {
        investmentInfoRowsProps.unshift({
            label: 'Return earned',
            value: toDollarsWithCents(returnAmount),
            infoTitle: 'Return earned',
            infoParagraphs: [
                'Return earned is the amount you’ve earned on your contributions so far. Money market funds have interest and dividend returns paid out monthly, just like savings accounts. Returns are usually received in the first week of each month.',
            ],
            infoFormulaItems:
                depositsAmount > 0
                    ? [
                          {
                              type: 'term',
                              value: toDollarsWithCents(returnAmount),
                              title: 'return earned',
                          },
                          { type: 'op', value: '=' },
                          {
                              type: 'term',
                              value: toDollarsWithCents(balance),
                              title: 'balance',
                          },
                          { type: 'op', value: '-' },
                          {
                              type: 'term',
                              value: toDollarsWithCents(netInvestedAmount),
                              title: 'net contributed',
                          },
                      ]
                    : undefined,
        });

        investmentInfoRowsProps.push({
            label: 'Investment type',
            value: 'Money market fund',
        });

        if (currentRateInfo) {
            investmentInfoRowsProps.push({
                label: 'APY after fees',
                value: getMoneyMarketRateStr(currentRateInfo),
                valueHighlightColorScheme: 'green',
                infoTitle: 'APY after fees',
                infoParagraphs: mmfReturnDisclosureParagraphs,
            });
        }
    }

    if (
        investmentInfo.investmentType === 'DIRECT_INDEXING' ||
        investmentInfo.investmentType === 'CLASSIC_PORTFOLIO'
    ) {
        const timeWeightedROR = investmentInfo.timeWeightedReturn;
        const timeWeightedRORStr = isNumber(timeWeightedROR)
            ? toPercentageStr(timeWeightedROR)
            : '0%';

        investmentInfoRowsProps.unshift(
            {
                label: 'Return',
                value: toPercentageStr(rateOfReturn),
                infoTitle: 'Return on investment',
                infoParagraphs: [
                    'Return on investment (ROI) compares the gain or loss of an investment to its cost. It is calculated by comparing your net invested amount (total deposits minus total withdrawals) to your goal account’s current balance.',
                    'One disadvantage of ROI is that it doesn’t account for how long your investments were held.',
                ],
                infoFormulaItems:
                    depositsAmount > 0
                        ? [
                              {
                                  type: 'term',
                                  value: toPercentageStr(rateOfReturn),
                                  title: 'return',
                              },
                              { type: 'op', value: '=' },
                              { type: 'op', value: '(' },
                              { type: 'op', value: '(' },
                              {
                                  type: 'term',
                                  value: toDollarsWithCents(balance),
                                  title: 'balance',
                              },
                              { type: 'op', value: '+' },
                              {
                                  type: 'term',
                                  value: toDollarsWithCents(withdrawalsAmount),
                                  title: 'withdrawals',
                              },
                              { type: 'op', value: ')' },
                              { type: 'op', value: '/' },
                              {
                                  type: 'term',
                                  value: toDollarsWithCents(depositsAmount),
                                  title: 'deposits',
                              },
                              { type: 'op', value: '- 1' },
                              { type: 'op', value: ')' },
                              { type: 'op', value: 'x 100' },
                          ]
                        : undefined,
            },
            {
                label: 'Time-weighted return',
                value: timeWeightedRORStr,
                infoTitle: 'Time-weighted return',
                infoParagraphs: [
                    'Time-weighted return (TWR) is a way of calculating return to understand how your investments have performed over a period of time. It removes the impact of the timing of additional deposits or withdrawal, making it possible to compare this goal with another portfolio that may have different characteristics (like initial deposit amounts, deposit frequency, etc.).',
                ],
            }
        );
        investmentInfoRowsProps.push({
            label: 'Investment type',
            value: titleByGoalAccountInvestmentType[
                investmentInfo.investmentType
            ],
            valueIconName:
                iconByGoalAccountInvestmentType[investmentInfo.investmentType],
        });
    }

    const title = `${capitalize(posessiveStr)} ${
        goalAccount?.info.accountType === 'TRADITIONAL_IRA'
            ? 'Traditional IRA'
            : goalAccount?.info.accountType === 'ROTH_IRA'
              ? 'Roth IRA'
              : 'goal account'
    } so far`;

    return { investmentInfoRowsProps, title };
};

export const mmfReturnDisclosureParagraphs = [
    'APY is short for “annual percentage yield,” which is the annual interest rate you earn by putting your money into this account. You don’t have to wait a whole year for your return, though - interest is paid into your account every month.',
    'This rate moves with the fed funds rate, and is subject to change.',
    'The quoted rate is after (net of) Plenty’s 0.2% management fee. The rate you see is what you get - no tricks.',
];
